import React, { useState, useEffect } from "react";
import { Button, Grid, FormControl, MenuItem, Card, IconButton, Select, } from "@mui/material";
import { ArrowDropDown } from "@mui/icons-material";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import MDBox from "ui/MDBox";
import MDTypography from "ui/MDTypography";
import MDInput from "ui/MDInput";
import MDAlert from "ui/MDAlert";
import meetingServices from "services/meeting-services";
import centerService from "services/center-service";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import { UpdateButton } from "layouts/common/buttons";
import { useParams } from "react-router-dom";


const EditMeeting = () => {
  const { id } = useParams();

  const [loading, setLoading] = useState(false);
  const [notification, setNotification] = useState(null);
  const [centers, setCenters] = useState([]);
  const [meetingData, setMeetingData] = useState({
    title: "",
    date: "",
    center: "",
    level: "",
    venue: "",
  });
  const [errors, setErrors] = useState({
    titleError: false,
    dateError: false,
    centerError: false,
    levelError: false,
  });

  // Auto-hide notification
  useEffect(() => {
    if (notification) {
      const timer = setTimeout(() => setNotification(null), 4000);
      return () => clearTimeout(timer);
    }
  }, [notification]);

  // Fetch meeting by ID
  const getMeetingData = async () => {
    try {
      const response = await meetingServices.get(id);
      if (response.success && response.meeting) {
        const m = response.meeting;
        setMeetingData({
          title: m.title || "",
          date: m.date || "",
          center: m.center || "",
          level: m.level || "",
          venue: m.venue || "",
        });
      } else {
        console.error("Failed to fetch meeting:", response.message);
      }
    } catch (error) {
      console.error("Error fetching meeting:", error);
    }
  };

  // Fetch all centers
  const fetchCenters = async () => {
    try {
      const centerData = await centerService.getAll();
      if (centerData.success && centerData.centers) {
        setCenters(centerData.centers);
      }
    } catch (error) {
      console.error("Error fetching centers:", error);
    }
  };

  useEffect(() => {
    getMeetingData();
    fetchCenters();
  }, []);

  // Input handlers
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setMeetingData((prev) => ({ ...prev, [name]: value }));
  };

  const handleCenterChange = (e) => {
    const selectedCenterId = e.target.value;
    const selectedCenter = centers.find((c) => c.id === selectedCenterId);
    setMeetingData((prev) => ({
      ...prev,
      center: selectedCenterId,
      venue: selectedCenter ? selectedCenter.name : "",
    }));
  };

  const handleLevelChange = (e) => {
    const { value } = e.target;
    setMeetingData((prev) => ({ ...prev, level: value }));
  };

  // Submit handler
  const submitHandler = async (e) => {
    e.preventDefault();

    // Reset errors
    setErrors({
      titleError: false,
      dateError: false,
      centerError: false,
      levelError: false,
    });

    // Validation
    if (!meetingData.title.trim())
      return setErrors((p) => ({ ...p, titleError: true }));
    if (!meetingData.date.trim())
      return setErrors((p) => ({ ...p, dateError: true }));
    if (!meetingData.center.trim())
      return setErrors((p) => ({ ...p, centerError: true }));
    if (!meetingData.level.trim())
      return setErrors((p) => ({ ...p, levelError: true }));

    const updatedData = {
      title: meetingData.title,
      date: meetingData.date,
      center: meetingData.center,
      level: meetingData.level,
      venue: meetingData.venue,
    };

    setLoading(true);
    try {
      const response = await meetingServices.update(id, updatedData);
      if (response.success) {
        setNotification({ type: "success", message: "Meeting updated successfully!" });
      } else {
        setNotification({ type: "error", message: response.message || "Failed to update meeting." });
      }
    } catch (error) {
      console.error("Update failed:", error);
      setNotification({ type: "error", message: "API call failed. Check console." });
    } finally {
      setLoading(false);
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <MDTypography variant="h6" color="white">
                  Edit Meeting
                </MDTypography>
                <UpdateButton loading={loading} onClick={submitHandler} />
              </MDBox>

              {notification && (
                <MDAlert color={notification.type} mt="20px" sx={{ mx: 3 }}>
                  <MDTypography variant="body2" color="white">
                    {notification.message}
                  </MDTypography>
                </MDAlert>
              )}

              <MDBox
                component="form"
                onSubmit={submitHandler}
                display="flex"
                flexDirection="column"
                py={0}
                px={3}
              >
                <MDBox display="flex" flexDirection="row" mt={5} mb={3}>
                  <MDBox display="flex" flexDirection="column" alignItems="flex-start" width="100%" mr={2}>
                    <MDTypography variant="body2" ml={1}>
                      Title
                    </MDTypography>
                    <MDBox mb={2} width="100%">

                      <MDInput
                        type="text"
                        fullWidth
                        name="title"
                        value={meetingData.title}
                        onChange={handleInputChange}
                        error={errors.titleError}
                      />
                      {errors.titleError && (
                        <MDTypography variant="caption" color="error">
                          Please provide a title
                        </MDTypography>
                      )}
                    </MDBox>
                  </MDBox>
                  <MDBox display="flex" flexDirection="column" alignItems="flex-start" width="100%" mr={2}>
                    <MDTypography variant="body2" color="text" ml={1} fontWeight="regular">
                      Date
                    </MDTypography>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DatePicker
                        value={meetingData.date ? dayjs(meetingData.date) : null}
                        minDate={dayjs()} // 👈 Prevent selecting past dates
                        onChange={(newValue) => {
                          if (newValue && newValue.isBefore(dayjs(), "day")) {
                            setNotification({ type: "error", message: "You cannot select a past date!" });
                            return;
                          }
                          setMeetingData((prev) => ({
                            ...prev,
                            date: newValue ? newValue.format("YYYY-MM-DD") : "",
                          }));
                        }}
                        renderInput={(params) => (
                          <MDBox mb={2} width="100%">
                            <MDInput
                              {...params}
                              fullWidth
                              error={errors.dateError}
                              helperText={errors.dateError ? "Please provide date" : ""}
                            />
                          </MDBox>
                        )}
                      />


                    </LocalizationProvider>
                  </MDBox>

                  <MDBox display="flex" flexDirection="column" alignItems="flex-start" width="100%" mr={2}>
                    <MDTypography variant="body2" ml={1}>
                      Center/Venue
                    </MDTypography>
                    <MDBox mb={1} width="100%">
                      <FormControl fullWidth>
                        <Select
                          value={meetingData.center || "default"}
                          onChange={handleCenterChange}
                          error={errors.centerError}
                          IconComponent={() => (
                            <IconButton>
                              <ArrowDropDown />
                            </IconButton>
                          )}
                        >
                          <MenuItem disabled value="default">
                            Select Center
                          </MenuItem>
                          {centers.map((center) => (
                            <MenuItem key={center.id} value={center.id}>
                              {center.name}
                            </MenuItem>
                          ))}
                        </Select>
                        {errors.centerError && (
                          <MDTypography variant="caption" color="error">
                            Please provide Center/Venue
                          </MDTypography>
                        )}
                      </FormControl>
                    </MDBox>
                  </MDBox>
                </MDBox>
                <MDBox display="flex" flexDirection="row" mb={3}>
                  <MDBox display="flex" flexDirection="column" alignItems="flex-start" width="32%" mr={2}>
                    <MDTypography variant="body2" ml={1}>
                      Meeting Level
                    </MDTypography>
                    <MDBox mb={2} width="100%">
                      <FormControl fullWidth>
                        <Select
                          name="level"
                          value={meetingData.level || "default"}
                          onChange={handleLevelChange}
                          error={errors.levelError}
                          IconComponent={() => (
                            <IconButton>
                              <ArrowDropDown />
                            </IconButton>
                          )}
                        >
                          <MenuItem disabled value="default">
                            Select Level
                          </MenuItem>
                          <MenuItem value="center">Center Level</MenuItem>
                          <MenuItem value="city">City Level</MenuItem>
                        </Select>
                        {errors.levelError && (
                          <MDTypography variant="caption" color="error">
                            Please provide level
                          </MDTypography>
                        )}
                      </FormControl>
                    </MDBox>
                  </MDBox>
                </MDBox>
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
    </DashboardLayout >
  );
};

export default EditMeeting;
